import {numberText} from "../utils/sanitize.js";

// Where a seat sits on the board, as percentages of the arena. Seats sit on an
// ellipse; the anchor seat (the device's own seat on a phone, seat 0 on the host
// board, whose device never has a seat) lands bottom-centre and the rest spread
// evenly around the ring. Pure math so the untested React shell only binds the
// numbers into CSS custom properties — the old stylesheet stopped at eight
// hard-coded positions and had none at all for seat one on the host board.
export function seatPosition({index = 0, count = 1, meIndex = -1} = {}) {
  const seats = Math.max(1, count);
  const anchor = Math.max(meIndex, 0);
  // 90° points straight down in screen coordinates, which is the bottom-centre spot.
  // The vertical centre sits below 50% because a seat's minion row hangs above its
  // card: the top of the ring needs more headroom than the bottom.
  const angle = Math.PI / 2 + ((index - anchor) / seats) * Math.PI * 2;
  return {
    x: Math.round((50 + 42 * Math.cos(angle)) * 10) / 10,
    y: Math.round((54 + 34 * Math.sin(angle)) * 10) / 10,
  };
}

// The small line under a seat's sips: mana, cards left, and whatever is standing in
// front of the player. Synced counters go through numberText, same as the rest of the
// board, because any device that knows the code can POST them.
export function seatMeta(player) {
  // An empty deck is said in words: it is the warning that fatigue starts next turn.
  const deck = player.deck.length ? `${player.deck.length} kvar` : "tom lek!";
  const parts = [`${numberText(player.mana)}/${numberText(player.maxMana)} mana`, deck];
  if (player.reflect) parts.push("spegel");
  if (player.dodge) parts.push("paraply");
  if (player.hangover > 0) parts.push(`bakfylla ${numberText(player.hangover)}`);
  if (player.rebound) parts.push("tvåvägsspegel");
  if (player.armor > 0) parts.push("underlägg");
  if (player.counterDraw) parts.push("kontraskål");
  if (player.lifeline) parts.push("livlina");
  if (player.drip > 0) parts.push(`långdrink ${numberText(player.drip)}`);
  return parts.join(" · ");
}

// The waiter's pencil on a beer mat: one stroke per sip, every fifth struck across the
// four before it. Strokes not yet drunk are drawn faint, so the row is also the distance
// left to the glass's end. A forged maxSips could ask for a thousand strokes, so past
// MAX_TALLY the tally draws that many and fills them in proportion.
const MAX_TALLY = 50;

export function tallyGroups(taken = 0, max = 30) {
  const glass = Math.max(1, Math.trunc(Number(max)) || 1);
  const strokes = Math.min(MAX_TALLY, glass);
  const drunk = Math.max(0, Math.min(glass, Number(taken) || 0));
  const lit = Math.round((drunk / glass) * strokes);
  return Array.from({length: Math.ceil(strokes / 5)}, (_, key) => {
    const size = Math.min(5, strokes - key * 5);
    return {key, size, lit: Math.max(0, Math.min(size, lit - key * 5))};
  });
}

// Up to four seats the ring is roomy and the board looks exactly as it always did.
// Past that every extra phone has to buy its elbow room somewhere, so the cards give
// up size and the arena gives up height. The alternative is what players actually
// reported: seats printed on top of each other, with everyone's minions hidden behind
// a neighbouring card.
//
// SEAT_CARD_WIDTH/HEIGHT are the box the stylesheet paints at --seat-scale: 1, and
// test/board-layout.test.cjs walks every pair of seats at every roster size and
// arena width to prove the two curves below keep those boxes apart. Change a number
// here or the card's size in styles.css and that test is what catches it.
export const SEAT_CARD_WIDTH = 190;
export const SEAT_CARD_HEIGHT = 176;

const COMFORTABLE_SEATS = 4;
const BASE_ARENA_HEIGHT = 430;
const MIN_SEAT_SCALE = 0.55;
// The board is one screen: a TV has no scrollbar anyone can reach, and on a 13" laptop
// — about 800px of viewport — the action bar was ending up below the fold at eight
// players. Everything that is not the arena (topbar, event log, action bar) measures
// about 253px once styles.css compacts it under `max-height: 860px`, so this ceiling
// keeps the tallest board at 540 + 253 = 793. Raising it means shrinking that chrome
// further first, and test/board-layout.test.cjs is what says so.
const MAX_ARENA_HEIGHT = 540;

function crowding(count) {
  return Math.max(0, Math.max(1, count) - COMFORTABLE_SEATS);
}

// How much of its full size a seat card keeps. Bound below because a card nobody can
// read from the sofa is no better than one hidden behind a neighbour.
export function seatScale(count = 1) {
  return Math.max(MIN_SEAT_SCALE, Math.round((1 - crowding(count) * 0.1) * 100) / 100);
}

// How tall the arena has to be for that roster. The ring's radii are percentages, so
// the arena's height is the only thing that turns into real pixels between the seats
// on the left and right of the ellipse — which is exactly where a big table stacks.
export function arenaHeight(count = 1) {
  return Math.min(MAX_ARENA_HEIGHT, BASE_ARENA_HEIGHT + crowding(count) * 44);
}

// arenaHeight is a floor, not the arena: the board's arena row takes whatever the
// screen has left, and on a TV that is nearly twice the proven box. Ten seats stayed at
// their crowded size in the middle of all that room, too small to read from the sofa.
// The ring's radii are percentages, so growing the cards by as much as the arena outgrew
// the proven box is the proven picture scaled up — every gap the overlap test measured
// grows with it. Height sets the factor; width caps it once the arena is narrower, for
// that factor, than the narrowest board the test walks. Never past full size.
// ARENA_INSET is `.players { inset }` in styles.css: the seats live inside it.
const ARENA_INSET = 18;
const NARROWEST_PROVEN_ARENA = 900;

export function seatFit({count = 1, width = 0, height = 0} = {}) {
  const tall = (height - ARENA_INSET * 2) / (arenaHeight(count) - ARENA_INSET * 2);
  const wide = (width - ARENA_INSET * 2) / (NARROWEST_PROVEN_ARENA - ARENA_INSET * 2);
  const room = Math.min(tall, wide, 1 / seatScale(count));
  // Floored, so rounding can only ever leave a seat smaller than the proof allows.
  return room > 1 ? Math.floor(room * 100) / 100 : 1;
}

// How many seats the ring can hold. The arena is about 500px tall inside and a seat
// card is ~97px at its smallest, so past ten the ellipse simply runs out of vertical
// room: at fifteen players it printed four pairs of cards on top of each other, and no
// second ring fixes it — measured, not guessed. Beyond this the board lays the seats
// out as a grid, which cannot overlap however many turn up.
export const RING_SEAT_LIMIT = 10;

export function boardLayoutFor(count = 0) {
  return count > RING_SEAT_LIMIT ? "grid" : "ring";
}
